import { useState, useRef, useEffect, useMemo, Fragment } from "react";
import { ChevronDown, ChevronUp, Plus, ArrowUp } from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import AgenticMark from "../../common/AgenticMark/AgenticMark";
import { avatarInitials } from "../../common/avatarInitials";
import { renderMarkdown } from "./markdown";
import ProviderComparisonCard from "./ProviderComparisonCard";

// Mirrors EMPTY_FORM in V2ProviderList.jsx — keep field shape identical so this
// posts to /api/v2/providers cleanly.
const buildEmptyProviderForm = (quoteType) => ({
	name: "",
	type: quoteType === "domestic_trucking" ? "CARRIER" : "FORWARDER",
	contactEmail: "",
	contactName: "",
	serviceType:
		quoteType === "domestic_trucking" ? "domestic_trucking" : "ocean_freight",
	// Decides whether an FTL or an LTL quote request reaches this carrier at
	// all, so a trucking carrier has to pick one before it can be created.
	truckingMode: "",
	preferredChannel: "email",
});

const handlesTrucking = (serviceType) =>
	serviceType === "domestic_trucking" || serviceType === "both";
const PROVIDER_TYPE_OPTIONS = [
	{ value: "FORWARDER", label: "Forwarder" },
	{ value: "CARRIER", label: "Carrier" },
	{ value: "BROKER", label: "Broker" },
];
const PROVIDER_SERVICE_OPTIONS = [
	{ value: "ocean_freight", label: "Ocean freight" },
	{ value: "domestic_trucking", label: "Domestic trucking" },
	{ value: "both", label: "Both" },
];
// Chip text for the mode a carrier is configured for. Every trucking carrier
// shows it, so the picker reads as a classification and not just a warning.
const TRUCKING_MODE_CHIP = {
	FTL: "FTL",
	LTL: "LTL",
	both: "FTL + LTL",
};

const TRUCKING_MODE_OPTIONS = [
	{ value: "FTL", label: "FTL only" },
	{ value: "LTL", label: "LTL only" },
	{ value: "both", label: "Both FTL and LTL" },
];

export function ProviderSelectionPanel({
	item,
	onSend,
	onAddManual,
	onMessage,
	onDismiss,
	variant = "inline",
	sending = false,
}) {
	const isFooter = variant === "footer";
	const candidates = item.candidates || [];
	const quoteType = item.quoteType || "ocean_freight";
	// Candidate set fingerprint — only changes when providers come/go, not when
	// their object references rotate (which happens on every activity refetch).
	// Using this as the dep avoids spurious resets of the user's check selections.
	const candidatesKey = useMemo(
		() =>
			candidates
				.map((c) => c.providerId)
				.sort()
				.join("|"),
		[candidates],
	);
	const initial = useMemo(() => {
		const sorted = [...candidates].sort((a, b) => b.score - a.score);
		return new Set(
			sorted.slice(0, item.suggestedCount).map((c) => c.providerId),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [candidatesKey, item.suggestedCount]);
	const [selected, setSelected] = useState(initial);
	// When the picker reopens with zero candidates (everyone's been contacted),
	// jump straight to the Add-provider form so the user has a clear path
	// forward instead of an empty list and a "0 selected" disabled Send.
	const [showAddForm, setShowAddForm] = useState(() => candidates.length === 0);
	const [newProvider, setNewProvider] = useState(() =>
		buildEmptyProviderForm(quoteType),
	);
	const [addError, setAddError] = useState(null);
	const userTouchedRef = useRef(false);
	const noCandidatesLeft = candidates.length === 0;

	useEffect(() => {
		// Only reseed selection from `initial` when the user hasn't toggled anything
		// yet. After the first manual toggle, refetches that rotate candidate refs
		// must not stomp the user's picks.
		if (userTouchedRef.current) return;
		setSelected(initial);
	}, [initial]);

	useEffect(() => {
		// Re-open the add form if a refetch leaves us with no candidates. Cheap
		// dependency on the count only — toggling a checkbox shouldn't trip this.
		if (noCandidatesLeft) setShowAddForm(true);
	}, [noCandidatesLeft]);

	useEffect(() => {
		setNewProvider(buildEmptyProviderForm(quoteType));
	}, [quoteType]);

	// Natural-language entry — text is forwarded to Marq (the chat agent), who
	// owns the intent parsing, provider matching, and RFQ dispatch. Frontend's
	// only job is to relay the message and let the existing assistant reply
	// flow handle everything else.
	const [nlInput, setNlInput] = useState("");
	const [nlSending, setNlSending] = useState(false);
	const submitNl = async () => {
		const text = nlInput.trim();
		if (!text || nlSending || typeof onMessage !== "function") return;
		// Clear immediately so the input feels responsive (mirrors the main
		// InputBar's handleSend pattern in V2WorkspacePage). On failure, restore
		// the typed text so the user can retry without retyping.
		setNlInput("");
		setNlSending(true);
		try {
			const ok = await onMessage(text);
			if (ok === false) setNlInput(text);
		} catch {
			setNlInput(text);
		} finally {
			setNlSending(false);
		}
	};

	const toggle = (id) => {
		userTouchedRef.current = true;
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const sortedCandidates = [...candidates].sort((a, b) => b.score - a.score);
	const count = selected.size;
	const hasAny = count >= 1;
	const overrideNudge =
		item.tenantMedianCount != null &&
		count > item.tenantMedianCount &&
		count > item.suggestedCount;

	const handleAddSubmit = () => {
		if (!newProvider.name || !newProvider.type || !newProvider.contactEmail) {
			setAddError("Name, type, and contact email are required.");
			return;
		}
		if (handlesTrucking(newProvider.serviceType) && !newProvider.truckingMode) {
			setAddError(
				"Pick whether this carrier handles FTL, LTL, or both — it decides which quote requests they receive.",
			);
			return;
		}
		const newId = `manual-${Date.now()}`;
		onAddManual(item.id, {
			providerId: newId,
			name: newProvider.name,
			score: 0,
			suggested: false,
			manuallyAdded: true,
			rationale: "Added manually",
			truckingMode: handlesTrucking(newProvider.serviceType)
				? newProvider.truckingMode
				: null,
			// shape matches POST /api/v2/providers; "" is not a legal truckingMode
			_formPayload: {
				...newProvider,
				truckingMode: handlesTrucking(newProvider.serviceType)
					? newProvider.truckingMode
					: null,
			},
		});
		setSelected((prev) => new Set([...prev, newId]));
		setNewProvider(buildEmptyProviderForm(quoteType));
		setAddError(null);
		setShowAddForm(false);
	};

	return (
		<div className={isFooter ? "ws-select-footer-panel" : "rb rb--creator"}>
			<div className={isFooter ? "ws-select-footer-head" : "rb__head"}>
				<div className={isFooter ? null : "rb__head-l"}>
					<span
						className={isFooter ? "ws-select-footer-eyebrow" : "rb__eyebrow"}
					>
						Pick providers to quote
					</span>
					{!isFooter && (
						<>
							<span style={{ color: "var(--neutral-100)" }}>·</span>
							<span className="rb__title">Pick providers to quote</span>
						</>
					)}
				</div>
				<div className={isFooter ? "ws-select-footer-head-r" : "rb__head-r"}>
					<span className="rb__meta">{count} selected</span>
				</div>
			</div>
			<div className={isFooter ? "ws-select-footer-body" : "rb__body"}>
				{noCandidatesLeft && (
					<div className="ws-select-empty-state">
						You've already contacted every provider in your list for this
						shipment. Add a new one below to keep going.
					</div>
				)}
				{sortedCandidates.map((c) => {
					const isOn = selected.has(c.providerId);
					return (
						<div
							key={c.providerId}
							className="ws-select-row"
							onClick={() => toggle(c.providerId)}
						>
							<input
								type="checkbox"
								className="ws-select-checkbox"
								checked={isOn}
								onChange={() => toggle(c.providerId)}
								onClick={(e) => e.stopPropagation()}
							/>
							<div className="ws-select-content">
								<div className="ws-select-row-name">
									{c.name}
									{c.suggested && (
										<span className="ws-select-suggested-badge">Suggested</span>
									)}
									{c.manuallyAdded && (
										<span className="ws-select-manual-badge">
											Added manually
										</span>
									)}
									{c.modeMismatch ? (
										<span
											className="ws-select-mode-badge"
											title={c.modeMismatchReason || undefined}
										>
											{c.truckingMode} only
										</span>
									) : (
										TRUCKING_MODE_CHIP[c.truckingMode] && (
											<span
												className="ws-select-mode-chip"
												data-testid="provider-mode-chip"
											>
												{TRUCKING_MODE_CHIP[c.truckingMode]}
											</span>
										)
									)}
								</div>
								<div className="ws-select-row-rationale">{c.rationale}</div>
								{/* v2_067: a mode mismatch is shown, never hidden — the user
								    can still pick this carrier, and a stale carrier
								    configuration only becomes visible here. */}
								{c.modeMismatch && c.modeMismatchReason && (
									<div className="ws-select-row-mode-note">
										{c.modeMismatchReason}
									</div>
								)}
							</div>
						</div>
					);
				})}

				{!showAddForm && (
					<button
						className="ws-select-add-link"
						onClick={() => setShowAddForm(true)}
					>
						<Plus size={14} strokeWidth={2} />
						Add provider not in your list
					</button>
				)}
				{showAddForm && (
					<div className="ws-select-add-form">
						<div className="ws-add-form-field ws-add-form-field--full">
							<span className="ws-add-form-label">
								Name <span style={{ color: "var(--text-accent)" }}>*</span>
							</span>
							<input
								value={newProvider.name}
								placeholder="e.g. Flexport"
								onChange={(e) =>
									setNewProvider({ ...newProvider, name: e.target.value })
								}
							/>
						</div>
						<div className="ws-add-form-field">
							<span className="ws-add-form-label">
								Type <span style={{ color: "var(--text-accent)" }}>*</span>
							</span>
							<select
								value={newProvider.type}
								onChange={(e) =>
									setNewProvider({ ...newProvider, type: e.target.value })
								}
							>
								{PROVIDER_TYPE_OPTIONS.map((o) => (
									<option key={o.value} value={o.value}>
										{o.label}
									</option>
								))}
							</select>
						</div>
						<div className="ws-add-form-field">
							<span className="ws-add-form-label">Service type</span>
							<select
								value={newProvider.serviceType}
								onChange={(e) =>
									setNewProvider({
										...newProvider,
										serviceType: e.target.value,
									})
								}
							>
								{PROVIDER_SERVICE_OPTIONS.map((o) => (
									<option key={o.value} value={o.value}>
										{o.label}
									</option>
								))}
							</select>
						</div>
						{handlesTrucking(newProvider.serviceType) && (
							<div className="ws-add-form-field">
								<span className="ws-add-form-label">
									Truckload mode{" "}
									<span style={{ color: "var(--text-accent)" }}>*</span>
								</span>
								<select
									value={newProvider.truckingMode}
									onChange={(e) =>
										setNewProvider({
											...newProvider,
											truckingMode: e.target.value,
										})
									}
								>
									<option value="" disabled>
										Select truckload mode
									</option>
									{TRUCKING_MODE_OPTIONS.map((o) => (
										<option key={o.value} value={o.value}>
											{o.label}
										</option>
									))}
								</select>
							</div>
						)}
						<div className="ws-add-form-field">
							<span className="ws-add-form-label">
								Contact email{" "}
								<span style={{ color: "var(--text-accent)" }}>*</span>
							</span>
							<input
								type="email"
								value={newProvider.contactEmail}
								placeholder="quotes@provider.com"
								onChange={(e) =>
									setNewProvider({
										...newProvider,
										contactEmail: e.target.value,
									})
								}
							/>
						</div>
						<div className="ws-add-form-field">
							<span className="ws-add-form-label">Contact name</span>
							<input
								value={newProvider.contactName}
								onChange={(e) =>
									setNewProvider({
										...newProvider,
										contactName: e.target.value,
									})
								}
							/>
						</div>
						<div className="ws-add-form-field ws-add-form-field--full">
							<span className="ws-add-form-label">Channel</span>
							<select
								value={newProvider.preferredChannel}
								onChange={(e) =>
									setNewProvider({
										...newProvider,
										preferredChannel: e.target.value,
									})
								}
							>
								<option value="email">Email</option>
								{/* <option value="whatsapp">WhatsApp</option>
                <option value="sms">SMS</option> */}
							</select>
						</div>
						{addError && (
							<div
								style={{
									gridColumn: "1 / -1",
									color: "var(--red-500)",
									fontSize: 12,
								}}
							>
								{addError}
							</div>
						)}
						<div className="ws-select-add-form-actions">
							<button
								className="ws-btn ws-btn--outline ws-btn--small"
								onClick={() => {
									setShowAddForm(false);
									setNewProvider(buildEmptyProviderForm(quoteType));
									setAddError(null);
								}}
							>
								Cancel
							</button>
							<button
								className="ws-btn ws-btn--primary ws-btn--small"
								onClick={handleAddSubmit}
							>
								Add provider
							</button>
						</div>
					</div>
				)}
			</div>
			<div className={isFooter ? "ws-select-footer-foot" : "rb__foot"}>
				<span className="rb__meta">
					{hasAny
						? `Sending to ${count} provider${count === 1 ? "" : "s"}${
								overrideNudge
									? ` · more than your usual ${item.tenantMedianCount}`
									: ""
							}`
						: "Pick at least one provider to continue"}
				</span>
				<div className="ws-select-footer-foot-actions">
					{typeof onDismiss === "function" && (
						<button
							type="button"
							className="ws-btn ws-btn--outline"
							onClick={onDismiss}
							disabled={sending}
						>
							Done
						</button>
					)}
					<button
						className="ws-btn ws-btn--primary"
						disabled={!hasAny || sending}
						onClick={() => onSend([...selected])}
					>
						{sending
							? "Sending quote requests..."
							: isFooter
								? `Send quote request${count === 1 ? "" : "s"} to ${count} provider${count === 1 ? "" : "s"}`
								: `Send to ${count} provider${count === 1 ? "" : "s"}`}
					</button>
				</div>
			</div>
			{typeof onMessage === "function" && isFooter && (
				<>
					{nlSending && (
						<div className="ws-presence-inline ws-presence-inline--in-picker">
							<AgenticMark size={32} state="working" ink="#22201C" />
							<span className="ws-shimmer-thought">Marq is thinking…</span>
						</div>
					)}
					<div className="ws-input-bar ws-input-bar--in-picker">
						<input
							className="ws-input"
							placeholder="Type a message…"
							value={nlInput}
							onChange={(e) => setNlInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && !e.shiftKey) {
									e.preventDefault();
									submitNl();
								}
							}}
							disabled={nlSending}
						/>
						<button
							className="ws-send-btn"
							onClick={submitNl}
							disabled={nlSending || !nlInput.trim()}
							aria-label="Send"
						>
							<ArrowUp size={18} strokeWidth={2.2} />
						</button>
					</div>
				</>
			)}
		</div>
	);
}

export function QuoteCard({
	item,
	onAccept,
	onViewPdf,
	accepting = false,
	viewingPdf = false,
	selectionLocked = false,
}) {
	const [expanded, setExpanded] = useState(false);
	const isAccepted = item.status === "SELECTED" || item.selected === true;
	const isRejected = item.status === "REJECTED" || item.status === "DECLINED";
	const acceptDisabled =
		!item.quoteId || accepting || isAccepted || isRejected || selectionLocked;
	const acceptLabel = isAccepted
		? "Accepted"
		: isRejected || selectionLocked
			? "Not selected"
			: accepting
				? "Accepting..."
				: "Accept";
	const totalCost = Number(item.totalCost);
	const totalCostLabel = Number.isFinite(totalCost)
		? `$${totalCost.toLocaleString()}`
		: "Cost pending";
	const transitLabel = item.transitDays
		? `${item.transitDays} days transit`
		: "Transit pending";
	const renderRows = (rows) => (
		<div className="ws-quote-rows">
			{rows.map(([k, v]) => (
				<span key={k} className="ws-quote-row">
					<span className="ws-quote-key">{k}</span>
					<span className="ws-quote-val">{v}</span>
				</span>
			))}
		</div>
	);
	return (
		<div
			className={`rb rb--quote${isAccepted ? " is-accepted" : ""}${isRejected ? " is-not-selected" : ""}`}
		>
			<div className="rb__head">
				<div className="rb__head-l">
					<span className="rb__eyebrow">
						{isAccepted
							? "Quote accepted"
							: isRejected
								? "Quote not selected"
								: "Quote received"}
					</span>
					<span style={{ color: "var(--neutral-100)" }}>·</span>
					<span className="rb__title">{item.provider}</span>
					{isAccepted && (
						<span className="ws-quote-state-badge ws-quote-state-badge--accepted">
							Booked
						</span>
					)}
					{isRejected && (
						<span className="ws-quote-state-badge">Not selected</span>
					)}
				</div>
				<div className="rb__head-r">
					<span className="rb__meta">{item.received}</span>
				</div>
			</div>
			<div className="rb__body">
				<div className="ws-quote-headline">
					<span className="ws-quote-cost">
						{totalCostLabel}
						{item.currency && item.currency !== "USD" && (
							<span className="ws-quote-cost-currency"> {item.currency}</span>
						)}
					</span>
					<span className="ws-quote-cost-suffix">/ {transitLabel}</span>
				</div>
				<button
					className="ws-quote-toggle"
					onClick={() => setExpanded(!expanded)}
					aria-expanded={expanded}
				>
					{expanded ? (
						<ChevronUp size={14} strokeWidth={2} />
					) : (
						<ChevronDown size={14} strokeWidth={2} />
					)}
					{expanded ? "Hide details" : "Show full breakdown"}
				</button>
				{expanded &&
					(() => {
						const issues = Array.isArray(item.completionIssues)
							? item.completionIssues
							: [];
						const hasBreakdown = !!item.breakdown;
						const hasService = !!item.service;
						const hasTerms = Array.isArray(item.terms) && item.terms.length > 0;
						const hasReplySnippet =
							typeof item.replySnippet === "string" &&
							item.replySnippet.trim().length > 0;
						const hasAnyStructured = hasBreakdown || hasService || hasTerms;
						return (
							<>
								{hasBreakdown && (
									<div className="ws-quote-section">
										<div className="ws-quote-section-title">
											Pricing breakdown
										</div>
										{renderRows(item.breakdown)}
									</div>
								)}
								{hasService && (
									<div className="ws-quote-section">
										<div className="ws-quote-section-title">Service</div>
										{renderRows(item.service)}
									</div>
								)}
								{hasTerms && (
									<div className="ws-quote-section">
										<div className="ws-quote-section-title">Terms</div>
										{renderRows(item.terms)}
									</div>
								)}
								{/* {issues.length > 0 && (
									<div className="ws-quote-section ws-quote-section--issues">
										<div className="ws-quote-section-title">
											Things to verify
										</div>
										<ul className="ws-quote-issues">
											{issues.map((msg, i) => (
												<li key={i}>{msg}</li>
											))}
										</ul>
									</div>
								)} */}
								{hasReplySnippet && (
									<div className="ws-quote-section ws-quote-section--reply">
										<div className="ws-quote-section-title">
											Provider's reply
										</div>
										<blockquote className="ws-quote-reply">
											{item.replySnippet}
										</blockquote>
									</div>
								)}
								{!hasAnyStructured &&
									issues.length === 0 &&
									!hasReplySnippet && (
										<div className="ws-quote-empty-breakdown">
											No itemized breakdown available — the provider didn't
											include line items in the email. Open the PDF for full
											details.
										</div>
									)}
							</>
						);
					})()}
			</div>
			<div className="rb__foot">
				<span className="rb__meta" title={item.sourceDoc || undefined}>
					{isAccepted
						? "Selected for this shipment. Booking intent email sent."
						: isRejected
							? "Another quote was selected for this shipment."
							: "Auto-extracted from email"}
				</span>
				<div className="rb__foot-actions">
					<button
						className="ws-btn ws-btn--outline ws-btn--small"
						onClick={() => onViewPdf?.(item)}
						disabled={!item.quoteId || viewingPdf}
					>
						{viewingPdf ? "Opening..." : "View PDF"}
					</button>
					{/* Once a quote is accepted or rejected the action is final —
					   showing the button (even as "Accepted" or "Not selected")
					   suggests it's still clickable. Drop it entirely; the footer
					   meta text already communicates the state. */}
					{!isAccepted && !isRejected && (
						<button
							className="ws-btn ws-btn--primary ws-btn--small"
							onClick={() => onAccept?.(item)}
							disabled={acceptDisabled}
						>
							{acceptLabel}
						</button>
					)}
				</div>
			</div>
		</div>
	);
}

/**
 * BookingChoiceCard — inline card with two radios (Marq sends booking intent
 * email vs user contacts the carrier directly). The submit handler calls
 * up to the page, which posts to the backend; the backend then appends a
 * booking_sent / booking_handed_off activity that this component never
 * renders again because the parent re-keys on it.
 */
function BookingChoiceCard({ item, onSubmit, submitting, decided }) {
	// Once a terminal card (booking_sent / booking_handed_off) lands in the
	// activity feed, the parent passes a `decided` object with the method
	// that was committed. We mirror it here so the radio stays locked on
	// the right option and the submit button goes away — without this, the
	// card kept its "Send booking intent →" button live next to the final
	// "All yours" recap, looking like the choice was still pending.
	const [method, setMethod] = useState(decided?.method || "marq_sends");
	const locked = !!decided;
	useEffect(() => {
		if (decided?.method) setMethod(decided.method);
	}, [decided?.method]);
	const provider = item.providerName || "the carrier";
	const ccTarget = item.ownerEmail || "the workspace owner";
	return (
		<div>
			<div
				className={`rb rb--booking-choice${locked ? " is-locked" : ""}`}
				key={item.id}
			>
				<div className="rb__body">
					<label
						className={`ws-booking-option${method === "marq_sends" ? " is-selected" : ""}${locked ? " is-locked" : ""}`}
						htmlFor={`bookchoice-${item.id}-marq`}
					>
						{!locked && (
							<input
								id={`bookchoice-${item.id}-marq`}
								type="radio"
								name={`booking-method-${item.id}`}
								value="marq_sends"
								checked={method === "marq_sends"}
								onChange={() => setMethod("marq_sends")}
								disabled={locked}
							/>
						)}
						<div className="ws-booking-option-body">
							<div className="ws-booking-option-desc">
								I send an email confirming your intent to book with {provider}
							</div>
						</div>
					</label>
					<label
						className={`ws-booking-option${method === "direct" ? " is-selected" : ""}${locked ? " is-locked" : ""}`}
						htmlFor={`bookchoice-${item.id}-direct`}
					>
						{!locked && (
							<input
								id={`bookchoice-${item.id}-direct`}
								type="radio"
								name={`booking-method-${item.id}`}
								value="direct"
								checked={method === "direct"}
								onChange={() => setMethod("direct")}
								disabled={locked}
							/>
						)}
						<div className="ws-booking-option-body">
							<div className="ws-booking-option-desc">
								I stop here. You follow up with {provider}
							</div>
						</div>
					</label>
				</div>
				{!locked && (
					<div className="rb__foot">
						<div className="rb__foot-actions">
							<button
								type="button"
								className="ws-btn ws-btn--primary ws-btn--small"
								onClick={() => onSubmit?.(item, method)}
								disabled={submitting}
							>
								{submitting
									? "On it..."
									: method === "marq_sends"
										? "Send it →"
										: "I've got it →"}
							</button>
						</div>
					</div>
				)}
			</div>
			<div className="rb__head-l">
				<span className="rb__eyebrow">
					{locked
						? decided?.method === "marq_sends"
							? `Got it — I sent your quote to ${provider}.`
							: `Got it — you're handling ${provider} directly.`
						: `How should I close this out with ${provider}?`}
				</span>
			</div>
		</div>
	);
}

function BookingSentCard({ item }) {
	return (
		<div>
			<div className="ws-booking-sent-summary">Booking email sent.</div>
			<div className="rb rb--booking-sent" key={item.id}>
				<div className="rb__body">
					<div className="ws-booking-email-preview">
						{item.to && (
							<div className="ws-booking-email-row">
								<span className="ws-booking-email-label">To</span>
								<span className="ws-booking-email-value">{item.to}</span>
							</div>
						)}
						{item.cc && (
							<div className="ws-booking-email-row">
								<span className="ws-booking-email-label">Cc</span>
								<span className="ws-booking-email-value">{item.cc}</span>
							</div>
						)}
						{item.subject && (
							<div className="ws-booking-email-row">
								<span className="ws-booking-email-label">Subject</span>
								<span className="ws-booking-email-value">{item.subject}</span>
							</div>
						)}
						{item.bodySnippet && (
							<blockquote className="ws-quote-reply">
								{item.bodySnippet}
							</blockquote>
						)}
					</div>
					{item.emailError && (
						<div className="ws-booking-sent-error">
							Email failed to send: {item.emailError}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

// MTEU-746: the buyer's open carrier items (questions / needs-review),
// collected into one visually distinct block rendered directly below the
// comparison table. Each entry shows the summary (which carries the quoted
// amount when the same reply committed a price — MTEU-744), the extracted
// questions, the carrier's own words, and any parser issues.
function PendingCarrierBlock({ items }) {
	return (
		<div className="ws-pending-carrier">
			<div className="ws-pending-carrier-head">
				<span className="ws-pending-carrier-eyebrow">Needs your reply</span>
				<span className="ws-pending-carrier-count">
					{items.length} pending item{items.length === 1 ? "" : "s"}
				</span>
			</div>
			{items.map((item) => (
				<div className="ws-pending-carrier-item" key={item.id}>
					<div className="ws-system-text">{item.text}</div>
					{Array.isArray(item.openQuestions) && item.openQuestions.length > 0 && (
						<ul className="ws-pending-questions">
							{item.openQuestions.map((q, qIdx) => (
								<li key={qIdx}>{q}</li>
							))}
						</ul>
					)}
					{item.bodySnippet && (
						<blockquote className="ws-quote-reply ws-quote-reply--carrier">
							{item.bodySnippet}
						</blockquote>
					)}
					{Array.isArray(item.issues) && item.issues.length > 0 && (
						<ul className="ws-needs-review-issues">
							{item.issues.map((issue, issueIdx) => (
								<li key={issueIdx}>{issue}</li>
							))}
						</ul>
					)}
				</div>
			))}
		</div>
	);
}

export default function ActivityLog({
	shipment,
	draftConfirm,
	onConfirm,
	onAcceptQuote,
	onShare,
	onShareSelectionChange,
	onViewQuotePdf,
	onConfirmBookingIntent,
	submittingBookingChoice,
	acceptingQuoteId,
	viewingPdfQuoteId,
	isStreaming,
	showPresence,
	currentThought,
	typingTextById,
}) {
	const { user } = useUser();
	const userInitials = avatarInitials(user);
	const endRef = useRef(null);
	const lastShipmentIdRef = useRef(shipment.id);
	// Auto-scroll behaviour:
	//   - On shipment switch: jump to bottom (fresh context).
	//   - On new activity for the same shipment: only scroll if the user was
	//     already near the bottom. If they scrolled up to read older messages,
	//     don't yank them back — that "reset" feeling is what we're avoiding.
	const lastActivityId = shipment.activity[shipment.activity.length - 1]?.id;
	useEffect(() => {
		const log = endRef.current?.parentElement;
		if (!log) return;
		const switchedShipment = lastShipmentIdRef.current !== shipment.id;
		lastShipmentIdRef.current = shipment.id;
		if (switchedShipment) {
			log.scrollTop = log.scrollHeight;
			return;
		}
		const distanceFromBottom =
			log.scrollHeight - log.scrollTop - log.clientHeight;
		if (distanceFromBottom < 120) {
			log.scrollTop = log.scrollHeight;
		}
	}, [shipment.id, shipment.activity.length, lastActivityId]);
	// Identify the most recent AI message — that's the "live" one during streaming.
	const lastAiMsgId = useMemo(() => {
		for (let i = shipment.activity.length - 1; i >= 0; i--) {
			const a = shipment.activity[i];
			if (a.kind === "message" && a.role === "ai") return a.id;
		}
		return null;
	}, [shipment.activity]);

	// Find the terminal booking outcome so the booking_choice card can lock
	// its radios + drop the submit button. We infer the method from which
	// terminal kind landed — `booking_sent` means Marq did it, anything
	// else (`booking_handed_off`) means the user took the thread.
	const bookingDecision = useMemo(() => {
		for (let i = shipment.activity.length - 1; i >= 0; i--) {
			const a = shipment.activity[i];
			if (a.kind === "booking_sent") return { method: "marq_sends" };
			if (a.kind === "booking_handed_off") return { method: "direct" };
		}
		return null;
	}, [shipment.activity]);

	// Once the live comparison table is showing (≥2 providers contacted, ≥1
	// responded — same gate ProviderComparisonCard uses), it becomes THE decide
	// surface: we suppress the stacked per-quote cards and the snapshot compare
	// card so the table isn't competing with redundant views of the same quotes.
	const comparisonShown =
		(shipment.providers?.length || 0) >= 2 &&
		(shipment.providers || []).some((p) => p.status !== "pending");

	// The comparison table replaces the per-quote / compare cards. Render it
	// INLINE at the position of the last such item, so it sits chronologically
	// where the quotes arrived — and any later conversation (e.g. a share with
	// Marq) renders BELOW the table instead of above it. Falls back to the end
	// of the log if there are no quote/compare items to anchor to.
	let comparisonAnchorIdx = -1;
	if (comparisonShown) {
		for (let i = shipment.activity.length - 1; i >= 0; i--) {
			const k = shipment.activity[i].kind;
			if (k === "quote" || k === "compare") {
				comparisonAnchorIdx = i;
				break;
			}
		}
	}
	const comparisonCard = (
		<ProviderComparisonCard
			key="ws-pcompare"
			providers={shipment.providers}
			onAccept={onAcceptQuote}
			onShare={onShare}
			onSelectionChange={onShareSelectionChange}
			onViewPdf={onViewQuotePdf}
			viewingPdfQuoteId={viewingPdfQuoteId}
			acceptingQuoteId={acceptingQuoteId}
			selectionLocked={shipment.status === "BOOKED"}
		/>
	);

	// MTEU-746: carrier questions / needs-review items are the buyer's open
	// action items. Once the comparison table is up, they render in ONE
	// visually distinct block directly BELOW the table — the place the user
	// is already looking — instead of scattered through the thread where
	// they had to be hunted down (GCS call 2026-07-10). Without the table
	// (no quotes in yet) they keep their inline chronological position.
	const pendingCarrierItems = comparisonShown
		? shipment.activity.filter(
			(a) => a.kind === "carrier_question" || a.kind === "carrier_needs_review",
		)
		: [];
	const pendingCarrierBlock =
		pendingCarrierItems.length > 0 ? (
			<PendingCarrierBlock key="ws-pending-carrier" items={pendingCarrierItems} />
		) : null;

	return (
		<div className="ws-log">
			{shipment.activity.map((item, idx) => {
				if (comparisonShown && (item.kind === "quote" || item.kind === "compare"))
					return idx === comparisonAnchorIdx ? (
						<Fragment key="ws-pcompare-anchor">
							{comparisonCard}
							{pendingCarrierBlock}
						</Fragment>
					) : null;
				if (item.kind === "system") {
					return (
						<div className="ws-system" key={item.id}>
							{item.text}
						</div>
					);
				}
				if (item.kind === "booking_choice") {
					return (
						<BookingChoiceCard
							key={item.id}
							item={item}
							onSubmit={onConfirmBookingIntent}
							submitting={submittingBookingChoice}
							decided={bookingDecision}
						/>
					);
				}
				if (item.kind === "booking_sent") {
					return <BookingSentCard key={item.id} item={item} />;
				}
				if (item.kind === "booking_handed_off") return;
				if (
					item.kind === "carrier_question" ||
					item.kind === "carrier_needs_review"
				) {
					// MTEU-746: once the comparison table is up these render in the
					// pending block below it — suppress the inline copy so the same
					// item doesn't appear twice.
					if (comparisonShown) return null;
					return (
						<div
							className={`ws-system ws-system--carrier${item.kind === "carrier_needs_review" ? " ws-system--needs-review" : ""}`}
							key={item.id}
						>
							<div className="ws-system-text">{item.text}</div>
							{Array.isArray(item.openQuestions) &&
								item.openQuestions.length > 0 && (
									<ul className="ws-pending-questions">
										{item.openQuestions.map((q, qIdx) => (
											<li key={qIdx}>{q}</li>
										))}
									</ul>
								)}
							{item.bodySnippet && (
								<blockquote className="ws-quote-reply ws-quote-reply--carrier">
									{item.bodySnippet}
								</blockquote>
							)}
							{Array.isArray(item.issues) && item.issues.length > 0 && (
								<ul className="ws-needs-review-issues">
									{item.issues.map((issue, issueIdx) => (
										<li key={issueIdx}>{issue}</li>
									))}
								</ul>
							)}
						</div>
					);
				}
				if (item.kind === "carrier_declined") {
					// Show the carrier's actual words alongside the system summary so
					// the buyer doesn't read "review the carrier message" with no
					// message in sight. bodySnippet already has the email stripped of
					// quoted history by the backend. Declines are NOT pending items —
					// they stay inline chronologically.
					return (
						<div className="ws-system ws-system--carrier" key={item.id}>
							<div className="ws-system-text">{item.text}</div>
							{item.bodySnippet && (
								<blockquote className="ws-quote-reply ws-quote-reply--carrier">
									{item.bodySnippet}
								</blockquote>
							)}
						</div>
					);
				}
				if (item.kind === "quote") {
					return (
						<QuoteCard
							key={item.id}
							item={item}
							onAccept={onAcceptQuote}
							onViewPdf={onViewQuotePdf}
							accepting={acceptingQuoteId === item.quoteId}
							viewingPdf={viewingPdfQuoteId === item.quoteId}
							selectionLocked={shipment.status === "BOOKED"}
						/>
					);
				}
				// provider_selection items render as the page-level footer — not inline.
				if (item.kind === "provider_selection") return null;
				// pending_share + share_sent are state-only rows (MTEU-656): the
				// user-visible "Share these N quotes by email." turn and Marq's
				// "what email?" / "Sent." replies are written as their own
				// regular message rows. These rows just carry payload (quote IDs
				// / audit metadata) and have empty content — render nothing.
				if (item.kind === "pending_share" || item.kind === "share_sent") return null;
				if (item.kind === "compare") {
					const quotes = item.quotes || [];
					const fields = item.fields || [];
					const receivedQuotes = quotes.filter((q) => !q.declined);
					const gridTemplate = `minmax(140px, 1.4fr) ${quotes.map(() => "minmax(120px, 1fr)").join(" ")}`;
					return (
						<div className="rb rb--creator" key={item.id}>
							<div className="rb__head">
								<div className="rb__head-l">
									<span className="rb__eyebrow">Comparison</span>
									<span style={{ color: "var(--neutral-100)" }}>·</span>
									<span className="rb__title">
										{receivedQuotes.length} of {quotes.length} quotes received
									</span>
								</div>
								<div className="rb__head-r">
									<span className="rb__meta">Generated by Marq</span>
								</div>
							</div>
							<div className="rb__body">
								<div
									className="ws-compare-table"
									style={{ gridTemplateColumns: gridTemplate }}
								>
									<span className="ws-compare-corner" />
									{quotes.map((q) => (
										<span
											key={q.provider}
											className={`ws-compare-col-head ${q.declined ? "is-declined" : ""}`}
										>
											<span className="ws-compare-col-name">{q.provider}</span>
										</span>
									))}
									{fields.map((f) => (
										<Fragment key={f.key}>
											<span className="ws-compare-row-label">{f.label}</span>
											{quotes.map((q) => (
												<span
													key={q.provider}
													className={`ws-compare-cell ${q.declined ? "is-declined" : ""}`}
												>
													{q.declined ? (
														f.key === "cost" ? (
															<em style={{ color: "var(--text-tertiary)" }}>
																declined
															</em>
														) : (
															<span style={{ color: "var(--text-tertiary)" }}>
																—
															</span>
														)
													) : (
														(q[f.key] ?? (
															<span style={{ color: "var(--text-tertiary)" }}>
																—
															</span>
														))
													)}
												</span>
											))}
										</Fragment>
									))}
								</div>
								{quotes
									.filter((q) => q.declined)
									.map((q) => (
										<div key={q.provider} className="ws-compare-declined-note">
											<strong>{q.provider}:</strong> {q.declinedReason}
										</div>
									))}
							</div>
							<div className="rb__foot">
								<button className="ws-btn ws-btn--outline ws-btn--small">
									Open full comparison
								</button>
							</div>
						</div>
					);
				}
				if (item.kind === "booking") {
					return (
						<div className="rb rb--success" key={item.id}>
							<div className="rb__head">
								<div className="rb__head-l">
									<span
										className="rb__eyebrow"
										style={{ color: "var(--green-500)" }}
									>
										Booked
									</span>
									<span style={{ color: "var(--neutral-100)" }}>·</span>
									<span className="rb__title">{item.provider}</span>
								</div>
							</div>
							<div className="rb__body">
								<div className="ws-rail-rows">
									{item.containerNo && (
										<>
											<span className="ws-rail-key">Container</span>
											<span className="ws-rail-val">{item.containerNo}</span>
										</>
									)}
									{item.cutoff && (
										<>
											<span className="ws-rail-key">Cutoff</span>
											<span className="ws-rail-val">{item.cutoff}</span>
										</>
									)}
								</div>
							</div>
						</div>
					);
				}
				// message
				const role = item.role;
				const typedText = typingTextById?.[item.id];
				const displayText = typedText !== undefined ? typedText : item.text;
				const isStillTyping = typedText !== undefined;
				const isAi = role === "ai" || role === "assistant";
				// AI/system replies often contain markdown (**bold**, bullet lists,
				// line breaks). Customer turns are plain prose typed by the user, so
				// we render those verbatim — no parsing tax, no false bolds.
				const bubbleContent = isAi ? (
					<div className="ws-md">
						{renderMarkdown(displayText)}
						{isStillTyping && <span className="ws-typing-cursor" />}
					</div>
				) : (
					<>
						{displayText}
						{isStillTyping && <span className="ws-typing-cursor" />}
					</>
				);
				return (
					<div className={`ws-msg ws-msg--${role}`} key={item.id}>
						{role === "customer" && (
							<div className={`ws-avatar ws-avatar--${role}`}>
								{userInitials}
							</div>
						)}
						<div style={{ flex: role === "customer" ? "0 1 auto" : "1" }}>
							{role !== "customer" && role !== "ai" && role !== "assistant" && (
								<div className="ws-msg-author">
									<span>{item.author}</span>
								</div>
							)}
							<div className="ws-bubble">{bubbleContent}</div>
						</div>
					</div>
				);
			})}

			{/* Fallback: if the comparison is shown but there's no quote/compare
			   item to anchor to, render the table at the end of the log. When an
			   anchor exists, the table was already rendered inline above. The
			   pending-carrier block (MTEU-746) always rides directly below it. */}
			{comparisonShown && comparisonAnchorIdx === -1 && (
				<Fragment key="ws-pcompare-tail">
					{comparisonCard}
					{pendingCarrierBlock}
				</Fragment>
			)}

			{draftConfirm && (
				<div className="ws-card ws-card--success">
					<div className="ws-card-head">
						<div className="ws-card-title">Ready to book?</div>
					</div>
					<div style={{ fontSize: 13, marginBottom: 10 }}>{draftConfirm}</div>
					<div className="ws-card-actions">
						<button
							className="ws-btn ws-btn--primary ws-btn--small"
							onClick={onConfirm}
						>
							Confirm booking
						</button>
						<button className="ws-btn ws-btn--outline ws-btn--small">
							Hold
						</button>
					</div>
				</div>
			)}

			{showPresence && (
				<div className="ws-presence-inline">
					<AgenticMark size={50} state={isStreaming ? "working" : "rest"} />
					{isStreaming && currentThought && (
						<span className="ws-shimmer-thought">{currentThought}</span>
					)}
				</div>
			)}

			<div ref={endRef} />
		</div>
	);
}

export function InputBar({ value, onChange, onSend, disabled }) {
	return (
		<div className="ws-input-bar">
			<input
				className="ws-input"
				placeholder="Type a message…"
				value={value}
				onChange={(e) => onChange(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === "Enter" && !e.shiftKey) {
						e.preventDefault();
						onSend();
					}
				}}
				disabled={disabled}
			/>
			<button
				className="ws-send-btn"
				onClick={onSend}
				disabled={disabled || !value.trim()}
				aria-label="Send"
			>
				<ArrowUp size={18} strokeWidth={2.2} />
			</button>
		</div>
	);
}
